import {
	Box,
	Button,
	Card,
	CardActions,
	CardContent,
	CardHeader,
	Container,
	Divider,
	Grid,
	LinearProgress,
	MenuItem,
	Stack,
	Table,
	TableCell,
	TableRow,
	Typography,
} from "@mui/material";
import {
	Now,
	NumberField,
	PlainDate,
	PlainDatePicker,
	PlainDateTime,
	PlainDateTimePicker,
	TextField,
	ZonedDateTime,
} from "@variocube/app-ui";
import {IntegerNumberFormat} from "@variocube/app-ui/esm/Input/NumberField";
import React, {useEffect, useMemo, useState} from "react";
import {useAsync, useAsyncCallback} from "react-async-hook";
import {useNavigate} from "react-router-dom";
import {DisplayUnitsCount} from "../../booking-utils";
import {DiscountCheckResult, DiscountInput} from "../../component/discount-input";
import {ProlongError} from "../../component/prolong-error";
import {useBooking} from "../../context/booking";
import type {BookingBoxes} from "../../data/bookings";
import {B2Discount} from "../../data/client.gen";
import {BoxPricing, useSiteApi} from "../../data/sites";
import {getZoneDateTimeNow, parseZoneDateTimeFromDateTime} from "../../dates-utils";
import {useLocalization} from "../../i18n";
import {computePackages, DisplayCurrency} from "../../utils";
import {BookingHeader} from "./booking-header";

export function BookingProlongation() {
	const {t, language} = useLocalization();
	const navigate = useNavigate();
	const {booking, refresh} = useBooking();
	const {getPricing, prolongBooking} = useSiteApi();

	const [discount, setDiscount] = useState<B2Discount>();

	const [selectUnits, setSelectUnits] = useState(0);
	const [untilDate, setUntilDate] = useState<ZonedDateTime | null>(null);
	const [hours, setHours] = useState<number | null>(null);

	const site = useMemo(() => booking?.site, [booking?.site]);
	const fromDateTime = useMemo(() => {
		return booking?.until
			? parseZoneDateTimeFromDateTime(booking?.until as string, site?.zoneId ?? "UTC")
			: undefined;
	}, [booking]);
	const {
		result: {
			duration = 0,
			pricing = {},
			totalAmount = 0,
			discountAmount = 0,
			chargeAmount = 0,
		} = {},
	} = useAsync(async () => {
		let pricing: BoxPricing | undefined = undefined;
		let duration: number = 0;
		let totalAmount: number = 0;
		let discountAmount: number = 0;
		let chargeAmount: number = 0;

		if (booking && fromDateTime) {
			const site = booking.site;

			let from: ZonedDateTime | undefined = undefined;
			let until: ZonedDateTime | undefined = undefined;

			if (site.bookingUnit === "Days") {
				from = fromDateTime;
				until = (site.pricingMode === "Unit" && untilDate)
					? untilDate
					: fromDateTime.add({days: selectUnits - 1});
				duration = from.until(until).days + 1;
			}
			else if (site.bookingUnit === "Hours") {
				duration = site.pricingMode === "Unit" && hours
					? hours
					: selectUnits;
			}

			if (from && until || hours) {
				try {
					pricing = await getPricing(booking.tenant.id, booking.site.id, {
						from: from?.toPlainDate().toJSON(),
						until: until?.toPlainDate().toJSON(),
						fromDateTime: fromDateTime?.toJSON(),
						hours: hours ?? undefined,
					}) as BoxPricing;

					totalAmount = Object.entries(booking.boxes as BookingBoxes)
						.map(([type, qty]) => ((pricing ?? {})[type] ?? 0) * qty)
						.reduce((a, b) => a + b);

					if (discount) {
						discountAmount = Math.round((discount.value * totalAmount / 100) * 100) / 100;
					}

					chargeAmount = Math.max(0, totalAmount - discountAmount);
				}
				catch (err) {
					console.error("Failed to fetch box pricing", err);
				}
			}
		}
		return {
			duration,
			pricing,
			totalAmount,
			discountAmount,
			chargeAmount,
		};
	}, [booking, fromDateTime, untilDate, hours, selectUnits, discount]);

	useEffect(() => {
		if (booking && booking.paymentStatus === "open") {
			navigate(`/${booking.tenant.id}/bookings/${booking.id}/payment`);
		}
	}, [booking]);

	/**
	 * Automatically determinate the prolong unit if site pricing mode is Unit.
	 *
	 * The prolongation always starts at the current booking end (`fromDateTime`). For an already-ended booking
	 * that is in the past, so we default to "now"; for a still-active booking (deep-linked from the "ending soon"
	 * email) it is in the future, so we default to one unit past it — defaulting to "now" would be before the
	 * current end and rejected by the backend.
	 */
	useEffect(() => {
		if (fromDateTime && site?.pricingMode === "Unit") {
			const now = getZoneDateTimeNow(site.zoneId ?? "UTC");
			const isActive = ZonedDateTime.compare(fromDateTime, now) > 0;
			if (site?.bookingUnit === "Days") {
				setUntilDate(isActive ? fromDateTime.add({days: 1}) : now);
			}
			else if (site.bookingUnit === "Hours") {
				const target = isActive ? fromDateTime.add({hours: 1}) : now.add({hours: 1});
				setHours(fromDateTime.until(target, {largestUnit: "hours", smallestUnit: "hours"}).hours);
			}
		}
	}, [site, fromDateTime]);

	const packages = computePackages(site?.boxes ?? []);

	async function handleDiscountResult(result: DiscountCheckResult) {
		setDiscount(result.discountValid ? result.data : undefined);
	}

	const {error, loading, execute: handleProlongation} = useAsyncCallback(async () => {
		if (booking) {
			await prolongBooking(booking.id, {
				// The backend's B2Prolong.until is a LocalDate, so send the plain date (matching the pricing call
				// above) — ZonedDateTime.toJSON() would emit a zoned date-time the backend can't deserialize.
				until: untilDate?.toPlainDate().toJSON(),
				hours: hours ?? undefined,
				discountCode: discount?.code,
			});
			await refresh();
			// A free (zero-amount) prolongation is applied immediately with no payment, so return straight to the
			// booking view; a paid one continues to the payment page.
			navigate(
				chargeAmount > 0
					? `/${booking.tenant.id}/bookings/${booking.id}/payment`
					: `/${booking.tenant.id}/bookings/${booking.id}`,
			);
		}
	});

	function handlePackageChange(value: number) {
		setSelectUnits(value);
		if (booking) {
			if (site?.bookingUnit === "Days") {
				setUntilDate(parseZoneDateTimeFromDateTime(booking.from, site.zoneId ?? "UTC").add({days: value}));
			}
			else if (site?.bookingUnit === "Hours") {
				setHours(value);
			}
		}
	}

	function handleUntilDateChange(until: PlainDate | null) {
		const parsedUntil = until?.toZonedDateTime(site?.zoneId ?? "UTC");
		setUntilDate(parsedUntil ?? null);
	}

	return (
		<Container maxWidth="md">
			<Card sx={{mt: 2}}>
				<CardHeader
					title={<BookingHeader />}
				/>
				{booking !== undefined && (
					<CardContent>
						<Grid container spacing={2}>
							<Grid item xs={12} sm={8}>
								<Typography gutterBottom>{t("prolong.durationSelection")}</Typography>
								<Grid container spacing={2} sx={{mt: 1}}>
									<Grid item xs={12} sm={6}>
										{site?.bookingUnit === "Days" && (
											<PlainDatePicker
												required
												disabled
												fullWidth
												label={t("dates.from.label")}
												helperText={t("dates.from.hint")}
												value={fromDateTime?.toPlainDate() ?? null}
												onChange={() => {}}
											/>
										)}
										{site?.bookingUnit === "Hours" && (
											<PlainDateTimePicker
												required
												disabled
												fullWidth
												label={t("dates.from.label")}
												helperText={t("dates.from.hint")}
												value={fromDateTime?.toPlainDateTime() ?? null}
												onChange={() => {}}
											/>
										)}
									</Grid>
									<Grid item xs={12} sm={6}>
										{"Package" === site?.pricingMode && (
											<TextField
												select
												fullWidth
												required
												label={t("dates.package")}
												value={selectUnits}
												onChange={value => handlePackageChange(parseInt(value))}
												InputLabelProps={{
													shrink: true,
												}}
											>
												{packages.map(({units}, i) => (
													<MenuItem key={"package-" + i} value={units}>
														<DisplayUnitsCount unit={site.bookingUnit} count={units} />
													</MenuItem>
												))}
											</TextField>
										)}
										{"Unit" === site?.pricingMode && (
											<>
												{site?.bookingUnit === "Days" && (
													<PlainDatePicker
														required
														fullWidth
														label={t("dates.until.label")}
														helperText={t("dates.until.hint")}
														value={untilDate?.toPlainDate() ?? null}
														onChange={handleUntilDateChange}
														disablePast={true}
														minDate={fromDateTime?.toPlainDate()}
													/>
												)}
												{site?.bookingUnit === "Hours" && (
													<NumberField
														required
														fullWidth
														label={t("dates.hours")}
														numberFormat={IntegerNumberFormat}
														numberValue={hours}
														onChangeNumber={setHours}
														InputLabelProps={{
															shrink: true,
														}}
													/>
												)}
											</>
										)}
									</Grid>
									<Grid item xs={12}>
										<DiscountInput
											tenantId={booking.tenant.id}
											siteId={booking.site.id}
											onDiscountResult={handleDiscountResult}
										/>
									</Grid>
								</Grid>
							</Grid>
							<Grid item xs={12} sm={4}>
								<Typography variant="overline">{t("dates.duration")}</Typography>
								<Typography>
									<strong>
										<DisplayUnitsCount unit={site?.bookingUnit ?? "Days"} count={duration} />
									</strong>
								</Typography>
								<Typography variant="body2" color="textSecondary">
									{t("prolong.durationCalcHint")}
								</Typography>
								<Box my={2}>
									<Divider />
								</Box>
								<Typography variant="overline">{t("orders.steps.lockers")}</Typography>
								<Table size="small">
									{Object.entries(booking.boxes as BookingBoxes).map(([type, qty]) => {
										const boxPrice = (pricing[type] ?? 0) * qty;
										return (
											<TableRow key={type}>
												<TableCell sx={{border: "none", pl: 0}}>
													<Typography variant="subtitle1">
														<strong>{qty}x</strong>
													</Typography>
												</TableCell>
												<TableCell sx={{border: "none"}}>
													<Typography variant="body2">
														{t("boxes.short")} <strong>{type}</strong>
													</Typography>
												</TableCell>
												<TableCell sx={{border: "none", pr: 0}}>
													<Typography variant="body2" textAlign="right">
														{site?.currency ?? "EUR"} <DisplayCurrency amount={boxPrice} />
													</Typography>
												</TableCell>
											</TableRow>
										);
									})}
								</Table>
								<Box my={2}>
									<Divider />
								</Box>
								<Typography variant="overline">{t("payment.title")}</Typography>
								<Table size="small">
									<TableRow>
										<TableCell sx={{border: "none", pl: 0}}>
											<Typography variant="body2">{t("checkout.total")}</Typography>
										</TableCell>
										<TableCell sx={{border: "none", pr: 0}}>
											<Typography variant="body2" textAlign="right" whiteSpace="nowrap">
												{site?.currency ?? "EUR"} <DisplayCurrency amount={totalAmount} />
											</Typography>
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell sx={{border: "none", pl: 0}}>
											<Typography variant="body2">{t("discount.discount")}</Typography>
										</TableCell>
										<TableCell sx={{border: "none", pr: 0}}>
											<Typography variant="body2" textAlign="right" whiteSpace="nowrap">
												{site?.currency ?? "EUR"} <DisplayCurrency amount={discountAmount} />
											</Typography>
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell sx={{border: "none", pl: 0}}>
											<Typography variant="subtitle1">
												<strong>{t("checkout.charge")}</strong>
											</Typography>
										</TableCell>
										<TableCell sx={{border: "none", pr: 0}}>
											<Typography variant="subtitle1" textAlign="right" whiteSpace="nowrap">
												<strong>
													{site?.currency ?? "EUR"} <DisplayCurrency amount={chargeAmount} />
												</strong>
											</Typography>
										</TableCell>
									</TableRow>
								</Table>
							</Grid>
						</Grid>
					</CardContent>
				)}
				<CardActions>
					<Stack
						width="100%"
						direction="row"
						justifyContent="space-between"
					>
						<Button
							disabled={loading}
							onClick={() => navigate(`/${booking?.tenant.id}/bookings/${booking?.id}`)}
						>
							{t("actions.back")}
						</Button>
						<Button disabled={loading} variant="contained" onClick={handleProlongation}>
							{t("booking.prolong.button")}
						</Button>
					</Stack>
				</CardActions>
				{loading && <LinearProgress sx={{width: "100%"}} />}
			</Card>

			{error && <ProlongError error={error} />}
		</Container>
	);
}
